import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, isVisitExpired, type VisitEvent, type VisitRow } from "@/lib/api";

type Slot = { date: string; time: string };

function parseSlots(json: string | null): Array<{ date: string; time?: string }> {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    if (Array.isArray(v)) return v;
  } catch {}
  return [];
}

export default function AdminVisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const visitId = Number(id);
  const [visit, setVisit] = useState<VisitRow | null>(null);
  const [events, setEvents] = useState<VisitEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  // Approval form
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [duration, setDuration] = useState(60);
  const [message, setMessage] = useState("");
  // Denial form
  const [denyReason, setDenyReason] = useState("");
  const [mode, setMode] = useState<"approve" | "deny" | null>(null);
  // Publish form (events page)
  const [pubTitle, setPubTitle] = useState("");
  const [pubDesc, setPubDesc] = useState("");
  const [pubBusy, setPubBusy] = useState(false);
  const [pubMsg, setPubMsg] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.getVisit(visitId);
      setVisit(r.visit);
      setEvents(r.events);
      if (r.visit.status === "pending") {
        const slots = parseSlots(r.visit.requested_dates);
        if (slots[0]) {
          setSchedDate(slots[0].date);
          setSchedTime(slots[0].time || "10:00");
        }
      }
      setPubTitle(r.visit.event_title ?? "");
      setPubDesc(r.visit.event_description ?? "");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    if (Number.isFinite(visitId) && visitId > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  async function onApprove() {
    if (!schedDate) { setErr("Pick a scheduled date"); return; }
    setBusy(true); setErr(null);
    try {
      const iso = new Date(`${schedDate}T${schedTime || "10:00"}:00`).toISOString();
      await api.approve(visitId, { scheduled_date: iso, scheduled_duration_min: duration, message });
      await load();
      setMode(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Approve failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDeny() {
    setBusy(true); setErr(null);
    try {
      await api.deny(visitId, denyReason);
      await load();
      setMode(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Deny failed");
    } finally {
      setBusy(false);
    }
  }

  async function setPublished(is_public: boolean) {
    setPubBusy(true); setPubMsg(null);
    try {
      await api.publish(visitId, { is_public, event_title: pubTitle, event_description: pubDesc });
      await load();
      setPubMsg(is_public ? "Published to /events" : "Removed from /events");
    } catch (e) {
      setPubMsg(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setPubBusy(false);
    }
  }

  async function onArchive(archived: boolean) {
    const verb = archived ? "archive" : "restore";
    if (archived && !confirm("Archive this request? It will be hidden from the normal lists but can be restored from the Archived filter.")) return;
    setBusy(true); setErr(null);
    try {
      await api.archiveVisit(visitId, archived);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : `Could not ${verb}`);
    } finally {
      setBusy(false);
    }
  }

  if (!visit) return <div className="text-museum-muted">Loading…</div>;

  const preferred = parseSlots(visit.requested_dates);
  const alternates = parseSlots(visit.alternate_dates);
  const breakdown = visit.attendees_breakdown ? safeParse(visit.attendees_breakdown) : null;
  const expired = isVisitExpired(visit);
  const archived = !!visit.archived_at;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between text-sm">
        <button onClick={() => navigate(-1)} className="underline">← Back</button>
        {!editing && (
          <button className="btn-secondary" onClick={() => setEditing(true)}>Edit request</button>
        )}
      </div>

      {editing ? (
        <EditForm
          visit={visit}
          onCancel={() => setEditing(false)}
          onSaved={async () => { setEditing(false); await load(); }}
        />
      ) : (
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <div className="flex justify-between items-start gap-3">
              <div>
                <h1 className="text-xl font-serif">Request #{visit.id}</h1>
                <div className="text-sm text-museum-muted">
                  Submitted {new Date(visit.created_at + "Z").toLocaleString()}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {expired && <Pill className="bg-amber-100 text-amber-800">Expired</Pill>}
                {archived && <Pill className="bg-stone-200 text-stone-700">Archived</Pill>}
                <StatusBadge status={visit.status} />
              </div>
            </div>
          </div>

          <Section title="Contact">
            <KV label="Organization" value={visit.organization} />
            <KV label="Name" value={visit.contact_name} />
            <KV label="Email" value={<a className="underline" href={`mailto:${visit.contact_email}`}>{visit.contact_email}</a>} />
            <KV label="Phone" value={visit.contact_phone} />
            <KV label="Address" value={visit.address} />
            <KV label="IBEW affiliation" value={visit.ibew_affiliation} />
            {visit.volunteer_interest ? (
              <KV label="Volunteer interest" value={<span className="text-museum-accent2 font-medium">Wants to volunteer</span>} />
            ) : null}
          </Section>

          <Section title="Attendees">
            <KV label="Total" value={visit.attendees_count} />
            {breakdown && <KV label="Breakdown" value={`${breakdown.adults ?? "?"} adults / ${breakdown.children ?? "?"} children`} />}
            <KV label="Age ranges" value={visit.age_ranges} />
          </Section>

          <Section title="Visit details">
            <KV label="Tour type" value={visit.tour_type} />
            <KV label="Purpose" value={visit.purpose} block />
            <KV label="Accessibility" value={visit.accessibility} block />
            <KV label="Photo release" value={visit.photo_release ? "Yes" : "No"} />
            <KV label="Heard from" value={visit.heard_from} />
            <KV label="Internal notes" value={visit.notes} block />
          </Section>

          <Section title="Requested dates">
            <ul className="list-disc pl-5 text-sm">
              {preferred.map((s, i) => (<li key={i}>{s.date}{s.time ? ` at ${s.time}` : ""}</li>))}
            </ul>
            {alternates.length > 0 && <>
              <div className="font-medium text-sm mt-3">Alternates</div>
              <ul className="list-disc pl-5 text-sm">
                {alternates.map((s, i) => (<li key={i}>{s.date}{s.time ? ` at ${s.time}` : ""}</li>))}
              </ul>
            </>}
          </Section>

          {visit.status === "approved" && (
            <Section title="Confirmed schedule">
              <KV label="Date" value={new Date(visit.scheduled_date!).toLocaleString()} />
              <KV label="Duration" value={`${visit.scheduled_duration_min ?? 60} minutes`} />
              <KV label="Approved by" value={visit.approved_by} />
              <KV label="Approved at" value={visit.approved_at ? new Date(visit.approved_at).toLocaleString() : null} />
            </Section>
          )}

          {visit.status === "denied" && (
            <Section title="Denial">
              <KV label="Reason" value={visit.denied_reason} block />
              <KV label="By" value={visit.approved_by} />
            </Section>
          )}
        </div>

        <div className="space-y-4">
          {visit.status === "pending" && (
            <div className="card p-5 space-y-3">
              {mode !== "deny" && (
                <>
                  <h2 className="font-serif text-lg">Approve request</h2>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="label">Date</label>
                      <input type="date" className="input" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} />
                    </div>
                    <div>
                      <label className="label">Time</label>
                      <input type="time" className="input" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="label">Duration (minutes)</label>
                    <input type="number" min={15} max={600} className="input"
                           value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
                  </div>
                  <div>
                    <label className="label">Message to requester (optional)</label>
                    <textarea className="textarea" value={message} onChange={(e) => setMessage(e.target.value)} />
                  </div>
                  <button className="btn-primary w-full" disabled={busy} onClick={onApprove}>
                    {busy ? "Approving…" : "Approve & send confirmation"}
                  </button>
                  <button className="text-sm text-museum-muted underline w-full" onClick={() => setMode("deny")}>
                    Deny instead
                  </button>
                </>
              )}
              {mode === "deny" && (
                <>
                  <h2 className="font-serif text-lg">Deny request</h2>
                  <div>
                    <label className="label">Reason (optional, shared with requester)</label>
                    <textarea className="textarea" value={denyReason} onChange={(e) => setDenyReason(e.target.value)} />
                  </div>
                  <button className="btn-primary w-full" disabled={busy} onClick={onDeny}>
                    {busy ? "Sending…" : "Deny & email requester"}
                  </button>
                  <button className="text-sm text-museum-muted underline w-full" onClick={() => setMode(null)}>
                    Back
                  </button>
                </>
              )}
            </div>
          )}

          {visit.status === "approved" && (
            <div className="card p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-lg">Public events page</h2>
                <span className={"inline-block px-2 py-0.5 rounded text-xs " + (visit.is_public ? "bg-green-100 text-green-800" : "bg-stone-200 text-stone-700")}>
                  {visit.is_public ? "Published" : "Not public"}
                </span>
              </div>
              <p className="help">
                If published, this event appears at <code>/events</code> with the title and description below. Requester contact info is never shown.
              </p>
              <div>
                <label className="label">Public title</label>
                <input className="input" placeholder={visit.tour_type ?? "Museum visit"} value={pubTitle} onChange={(e) => setPubTitle(e.target.value)} />
              </div>
              <div>
                <label className="label">Description (optional)</label>
                <textarea className="textarea" placeholder="What visitors should expect…" value={pubDesc} onChange={(e) => setPubDesc(e.target.value)} />
              </div>
              <div className="flex gap-2">
                {!visit.is_public ? (
                  <button className="btn-primary w-full" disabled={pubBusy} onClick={() => setPublished(true)}>
                    {pubBusy ? "Publishing…" : "Publish to /events"}
                  </button>
                ) : (
                  <>
                    <button className="btn-secondary flex-1" disabled={pubBusy} onClick={() => setPublished(true)}>
                      {pubBusy ? "Saving…" : "Save changes"}
                    </button>
                    <button className="btn-ghost" disabled={pubBusy} onClick={() => setPublished(false)}>Unpublish</button>
                  </>
                )}
              </div>
              {pubMsg && <div className="text-xs text-museum-muted">{pubMsg}</div>}
            </div>
          )}

          <div className="card p-5 space-y-2">
            <h2 className="font-serif text-lg">Manage</h2>
            <p className="help">
              {archived
                ? "This request is archived — hidden from the normal lists. Restore it to bring it back."
                : "Archiving hides this request from the normal lists. It stays in the database and can be restored anytime."}
            </p>
            {archived ? (
              <button className="btn-secondary w-full" disabled={busy} onClick={() => onArchive(false)}>
                {busy ? "Working…" : "Restore from archive"}
              </button>
            ) : (
              <button className="btn-secondary w-full" disabled={busy} onClick={() => onArchive(true)}>
                {busy ? "Working…" : "Archive request"}
              </button>
            )}
            {archived && visit.archived_at && (
              <div className="text-xs text-museum-muted">
                Archived {new Date(visit.archived_at).toLocaleString()}{visit.archived_by ? ` by ${visit.archived_by}` : ""}
              </div>
            )}
          </div>

          <div className="card p-5">
            <h2 className="font-serif text-lg mb-2">Activity</h2>
            <ol className="space-y-2 text-sm">
              {events.map((e) => (
                <li key={e.id}>
                  <div className="text-xs text-museum-muted">
                    {new Date(e.created_at + "Z").toLocaleString()} · {e.actor_email ?? "system"}
                  </div>
                  <div><strong>{e.action}</strong>{e.detail ? `: ${e.detail}` : ""}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      )}

      {err && <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded">{err}</div>}
    </div>
  );
}

// ---- Edit form ----

function EditForm({ visit, onSaved, onCancel }: { visit: VisitRow; onSaved: () => void; onCancel: () => void }) {
  const bd = visit.attendees_breakdown ? safeParse(visit.attendees_breakdown) : null;
  const initSlots = (json: string | null, n: number): Slot[] => {
    const parsed = parseSlots(json).map((s) => ({ date: s.date ?? "", time: s.time ?? "" }));
    while (parsed.length < n) parsed.push({ date: "", time: "" });
    return parsed.slice(0, n);
  };

  const [organization, setOrganization] = useState(visit.organization ?? "");
  const [contactName, setContactName] = useState(visit.contact_name ?? "");
  const [contactEmail, setContactEmail] = useState(visit.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(visit.contact_phone ?? "");
  const [address, setAddress] = useState(visit.address ?? "");
  const [attendeesCount, setAttendeesCount] = useState(String(visit.attendees_count ?? ""));
  const [adults, setAdults] = useState(bd?.adults != null ? String(bd.adults) : "");
  const [children, setChildren] = useState(bd?.children != null ? String(bd.children) : "");
  const [ageRanges, setAgeRanges] = useState(visit.age_ranges ?? "");
  const [preferred, setPreferred] = useState<Slot[]>(initSlots(visit.requested_dates, 3));
  const [alternates, setAlternates] = useState<Slot[]>(initSlots(visit.alternate_dates, 3));
  const [tourType, setTourType] = useState(visit.tour_type ?? "");
  const [accessibility, setAccessibility] = useState(visit.accessibility ?? "");
  const [purpose, setPurpose] = useState(visit.purpose ?? "");
  const [photoRelease, setPhotoRelease] = useState<boolean>(!!visit.photo_release);
  const [heardFrom, setHeardFrom] = useState(visit.heard_from ?? "");
  const [ibew, setIbew] = useState(visit.ibew_affiliation ?? "");
  const [volunteer, setVolunteer] = useState<boolean>(!!visit.volunteer_interest);
  const [notes, setNotes] = useState(visit.notes ?? "");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function cleanSlots(slots: Slot[]) {
    return slots
      .filter((s) => s.date.trim() !== "")
      .map((s) => ({ date: s.date, time: s.time ? s.time : undefined }));
  }

  function setSlot(list: Slot[], setList: (s: Slot[]) => void, i: number, patch: Partial<Slot>) {
    setList(list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body = {
        organization,
        contact_name: contactName,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        address,
        attendees_count: Number(attendeesCount),
        attendees_adults: adults === "" ? undefined : Number(adults),
        attendees_children: children === "" ? undefined : Number(children),
        age_ranges: ageRanges,
        requested_dates: cleanSlots(preferred),
        alternate_dates: cleanSlots(alternates),
        tour_type: tourType,
        accessibility,
        purpose,
        photo_release: photoRelease,
        heard_from: heardFrom,
        ibew_affiliation: ibew,
        volunteer_interest: volunteer,
        notes,
      };
      await api.editVisit(visit.id, body);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save changes");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5 space-y-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-serif">Edit request #{visit.id}</h1>
        <button className="text-sm text-museum-muted underline" onClick={onCancel}>Cancel</button>
      </div>

      <FieldGrid>
        <Field label="Organization"><input className="input" value={organization} onChange={(e) => setOrganization(e.target.value)} /></Field>
        <Field label="Contact name"><input className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} /></Field>
        <Field label="Contact email"><input className="input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></Field>
        <Field label="Contact phone"><input className="input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} /></Field>
        <Field label="Mailing address" wide><input className="input" value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
        <Field label="Total attendees"><input type="number" min={1} className="input" value={attendeesCount} onChange={(e) => setAttendeesCount(e.target.value)} /></Field>
        <Field label="Adults (optional)"><input type="number" min={0} className="input" value={adults} onChange={(e) => setAdults(e.target.value)} /></Field>
        <Field label="Children (optional)"><input type="number" min={0} className="input" value={children} onChange={(e) => setChildren(e.target.value)} /></Field>
        <Field label="Age ranges (optional)"><input className="input" value={ageRanges} onChange={(e) => setAgeRanges(e.target.value)} /></Field>
        <Field label="Tour / program"><input className="input" value={tourType} onChange={(e) => setTourType(e.target.value)} /></Field>
        <Field label="Heard about us"><input className="input" value={heardFrom} onChange={(e) => setHeardFrom(e.target.value)} /></Field>
        <Field label="IBEW affiliation"><input className="input" value={ibew} onChange={(e) => setIbew(e.target.value)} /></Field>
      </FieldGrid>

      <div>
        <label className="label">Preferred dates (at least one)</label>
        <SlotEditor slots={preferred} onChange={(i, patch) => setSlot(preferred, setPreferred, i, patch)} />
      </div>
      <div>
        <label className="label">Alternate dates (optional)</label>
        <SlotEditor slots={alternates} onChange={(i, patch) => setSlot(alternates, setAlternates, i, patch)} />
      </div>

      <Field label="Purpose / description" wide><textarea className="textarea" value={purpose} onChange={(e) => setPurpose(e.target.value)} /></Field>
      <Field label="Accessibility needs" wide><textarea className="textarea" value={accessibility} onChange={(e) => setAccessibility(e.target.value)} /></Field>
      <Field label="Internal notes (staff-only)" wide><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>

      <div className="flex flex-wrap gap-6">
        <div>
          <label className="label">Photos OK to share?</label>
          <div className="flex gap-4 text-sm mt-1">
            <label className="flex items-center gap-1"><input type="radio" checked={photoRelease} onChange={() => setPhotoRelease(true)} /> Yes</label>
            <label className="flex items-center gap-1"><input type="radio" checked={!photoRelease} onChange={() => setPhotoRelease(false)} /> No</label>
          </div>
        </div>
        <div>
          <label className="label">Volunteer interest</label>
          <label className="flex items-center gap-2 text-sm mt-2">
            <input type="checkbox" checked={volunteer} onChange={(e) => setVolunteer(e.target.checked)} /> Wants to volunteer
          </label>
        </div>
      </div>

      {err && <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded text-sm">{err}</div>}

      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
        <button className="btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      <p className="help">Editing changes the stored request only — it does not email the requester.</p>
    </div>
  );
}

function SlotEditor({ slots, onChange }: { slots: Slot[]; onChange: (i: number, patch: Partial<Slot>) => void }) {
  return (
    <div className="space-y-2">
      {slots.map((s, i) => (
        <div key={i} className="grid grid-cols-2 gap-3 max-w-md">
          <input type="date" className="input" value={s.date} onChange={(e) => onChange(i, { date: e.target.value })} />
          <input type="time" className="input" value={s.time} onChange={(e) => onChange(i, { time: e.target.value })} />
        </div>
      ))}
    </div>
  );
}

function FieldGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function safeParse(s: string): any { try { return JSON.parse(s); } catch { return null; } }

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h2 className="font-serif text-lg mb-2">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
    </div>
  );
}

function KV({ label, value, block }: { label: string; value: React.ReactNode; block?: boolean }) {
  if (value == null || value === "") return null;
  return (
    <div className={block ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wide text-museum-muted">{label}</div>
      <div className="whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={`inline-block px-2 py-1 rounded text-xs ${className}`}>{children}</span>;
}

function StatusBadge({ status }: { status: VisitRow["status"] }) {
  const cls: Record<VisitRow["status"], string> = {
    pending:   "bg-amber-100 text-amber-800",
    approved:  "bg-green-100 text-green-800",
    denied:    "bg-stone-200 text-stone-700",
    cancelled: "bg-stone-200 text-stone-700",
  };
  return <span className={`inline-block px-2 py-1 rounded text-xs ${cls[status]}`}>{status}</span>;
}
